'use client';

import {
  applyTheme,
  isPremiumThemeId,
  getWallpaperStyles,
  getThemeDefaultWallpaperId,
} from '@/features/Preferences/data/themes/themes';
import {
  getWallpaperById,
  getWallpaperPreviewUrls,
} from '@/features/Preferences/data/wallpapers/wallpapers';
import usePreferencesStore from '@/features/Preferences/store/usePreferencesStore';
import { useClick } from '@/shared/hooks/generic/useAudio';
import useSessionScrollRestoration from '@/shared/hooks/generic/useSessionScrollRestoration';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, Palette } from 'lucide-react';
import { memo, useCallback, useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import CollapsibleSection from '@/features/Preferences/components/shared/CollapsibleSection';

interface ThemesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Local type definitions for lazy-loaded themes
interface Theme {
  id: string;
  displayName?: string;
  backgroundColor: string;
  cardColor: string;
  borderColor: string;
  mainColor: string;
  secondaryColor: string;
}

interface ThemeGroup {
  name: string;
  icon: LucideIcon;
  themes: Theme[];
}

interface ThemeCardProps {
  theme: {
    id: string;
    displayName?: string;
    backgroundColor: string;
    cardColor: string;
    borderColor: string;
    mainColor: string;
    secondaryColor: string;
  };
  isSelected: boolean;
  onClick: (id: string) => void;
}

const CHAOS_THEME_GRADIENT = `linear-gradient(
  142deg,
  oklch(66.0% 0.18 25.0 / 1) 0%,
  oklch(72.0% 0.22 80.0 / 1) 12%,
  oklch(68.0% 0.20 145.0 / 1) 24%,
  oklch(70.0% 0.19 200.0 / 1) 36%,
  oklch(67.0% 0.18 235.0 / 1) 48%,
  oklch(73.0% 0.22 290.0 / 1) 60%,
  oklch(69.0% 0.21 330.0 / 1) 74%,
  oklch(74.0% 0.20 355.0 / 1) 88%,
  oklch(66.0% 0.18 25.0 / 1) 100%
)`;

const ThemeCard = memo(function ThemeCard({
  theme,
  isSelected,
  onClick,
}: ThemeCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const themeName = theme.displayName ?? theme.id.replaceAll('-', ' ');
  const isChaosTheme = theme.id === '?';
  const isPremiumTheme = isPremiumThemeId(theme.id);
  const isBigBeautifulTheme = theme.id === 'big-beautiful-theme';

  // Check if theme has a default wallpaper (premium themes)
  const themeWallpaperId = getThemeDefaultWallpaperId(theme.id);
  const wallpaper = themeWallpaperId
    ? getWallpaperById(themeWallpaperId)
    : undefined;

  const background = isChaosTheme
    ? CHAOS_THEME_GRADIENT
    : isHovered
      ? theme.cardColor
      : theme.backgroundColor;

  const wallpaperStyles = wallpaper
    ? getWallpaperStyles(
        getWallpaperPreviewUrls(wallpaper).url,
        isHovered,
        getWallpaperPreviewUrls(wallpaper).urlWebp,
      )
    : {};

  const borderStyle = isPremiumTheme
    ? 'none'
    : `1px solid ${isSelected ? theme.mainColor : theme.borderColor}`;

  return (
    <button
      type='button'
      className={`rounded-lg p-3 text-left hover:cursor-pointer ${isBigBeautifulTheme ? 'col-span-2 row-span-2 min-h-[11rem]' : ''}`}
      style={{
        ...(wallpaper ? wallpaperStyles : { background }),
        border: borderStyle,
        outline: isSelected ? `3px solid ${theme.secondaryColor}` : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(theme.id)}
      aria-pressed={isSelected}
      aria-label={`Select ${themeName} theme`}
    >
      <div
        className={`mb-2 ${isPremiumTheme ? 'invisible h-8 overflow-hidden text-left' : ''}`}
      >
        {isChaosTheme ? (
          <span className='relative flex items-center justify-center text-sm text-white capitalize'>
            {/* <span
              className='absolute left-1/2 -translate-x-1/2'
              style={{ color: isSelected ? '#000' : 'transparent' }}
            >
              {'\u2B24'}
            </span> */}
            <span className='opacity-0'>?</span>
          </span>
        ) : (
          <span
            className={`capitalize ${isBigBeautifulTheme ? 'text-xl font-semibold' : 'text-sm'}`}
            style={{ color: theme.mainColor }}
          >
            {/* {isSelected && '\u2B24 '} */}
            {themeName}
          </span>
        )}
      </div>
      <div
        className='flex min-h-4 gap-1.5'
        style={{ visibility: isChaosTheme ? 'hidden' : 'visible' }}
      >
        <div
          className={`h-4 w-4 rounded-full ${isPremiumTheme ? 'hidden' : ''}`}
          style={{ background: theme.mainColor }}
        />
        <div
          className={`h-4 w-4 rounded-full ${isPremiumTheme ? 'hidden' : ''}`}
          style={{ background: theme.secondaryColor }}
        />
      </div>
    </button>
  );
});

export default function ThemesModal({ open, onOpenChange }: ThemesModalProps) {
  const { playClick } = useClick();
  const selectedTheme = usePreferencesStore(state => state.theme);
  const setSelectedTheme = usePreferencesStore(state => state.setTheme);

  // Lazy load themes
  const [themeSets, setThemeSets] = useState<ThemeGroup[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollRef, handleScroll } = useSessionScrollRestoration(
    'themes-modal-scroll',
    { enabled: open, ready: isLoaded },
  );

  useEffect(() => {
    if (open && !isLoaded) {
      import('@/features/Preferences/data/themes/themes').then(module => {
        setThemeSets(
          // Temporarily hide seasonal groups + Extra group in the modal.
          module.default.filter(
            group =>
              group.name !== 'Halloween' &&
              group.name !== 'Christmas' &&
              group.name !== 'Extra',
          ),
        );
        setIsLoaded(true);
      });
    }
  }, [open, isLoaded]);

  const handleThemeClick = useCallback(
    (themeId: string) => {
      playClick();
      setSelectedTheme(themeId);
      applyTheme(themeId);
    },
    [playClick, setSelectedTheme],
  );

  const handleClose = useCallback(() => {
    playClick();
    onOpenChange(false);
  }, [playClick, onOpenChange]);

  if (!open) return null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal forceMount>
        <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/80' />
        <DialogPrimitive.Content
          className='fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col gap-0 rounded-2xl border-0 border-(--border-color) bg-(--background-color) p-0 sm:max-h-[80vh] sm:w-[90vw]'
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <div className='sticky top-0 z-10 flex flex-row items-center justify-between rounded-t-2xl border-b border-(--border-color) bg-(--background-color) px-6 pt-6 pb-4'>
            <DialogPrimitive.Title className='flex items-center gap-2 text-2xl font-semibold text-(--main-color)'>
              <span className='motion-safe:animate-float flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-b-6 border-(--secondary-color-accent) bg-(--secondary-color) leading-none text-(--background-color) [--float-distance:-4px]'>
                <Palette size={22} />
              </span>
              Themes
            </DialogPrimitive.Title>
            <button
              onClick={handleClose}
              className='shrink-0 rounded-xl p-2 hover:cursor-pointer hover:bg-(--card-color) '
            >
              <X size={24} className='text-(--secondary-color) hover:text-(--secondary-color)' />
            </button>
          </div>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            data-scroll-restoration-id='container'
            className='flex-1 overflow-y-auto px-6 py-6'
          >
            <div className='space-y-6'>
              {themeSets.map(group => {
                const Icon = group.icon;
                const isPremiumGroup = group.name === 'Premium';
                const storageKey = `themes-modal-${group.name.toLowerCase().replace(/\s+/g, '-')}`;

                return (
                  <CollapsibleSection
                    key={group.name}
                    title={
                      isPremiumGroup ? (
                        <span>
                          <span className='text-(--main-color)'>Premium</span>
                          <span className='ml-1 text-(--secondary-color)'>
                            (experimental)
                          </span>
                        </span>
                      ) : (
                        <span className='text-(--main-color)'>{group.name}</span>
                      )
                    }
                    icon={<Icon size={16} />}
                    useNewIconDesign
                    level='subsection'
                    defaultOpen={true}
                    storageKey={storageKey}
                    className='gap-3'
                  >
                    <div className='grid grid-flow-row-dense grid-cols-2 gap-3 p-1 sm:grid-cols-3 md:grid-cols-4'>
                      {group.themes.map(theme => (
                        <ThemeCard
                          key={theme.id}
                          theme={theme}
                          isSelected={selectedTheme === theme.id}
                          onClick={handleThemeClick}
                        />
                      ))}
                    </div>
                  </CollapsibleSection>
                );
              })}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
